/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SWMLinkPage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ComponentLinkTemplate from "./ComponentLinkTemplate";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import CustomToolTip from "../../../infoButton/CustomToolTip";
import CustomSearchbarWithApiV2 from "../../common/searchBar/CustomSearchbarWithApiV2";
import { componentStyle } from "../../../../config/appTheme";
import CustomSelectV2 from "../../common/select/CustomSelectV2";
import CustomAutoCompleteWithAPIV2 from "../../common/searchBar/CustomAutoCompleteWithAPIV2";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import CustomDialogBoxV2 from "../../common/customDialogBox/CustomDialogBoxV2";
import {
  labCar,
  HandleApiActions,
  adminSettingsAPI,
} from "../../../../redux/actions";
import softwareIntersectionAPI from "../../../../redux/actions/softwareIntersectionActions";
import customConsole from "../../../../config/customConsole";
import { ENTITY } from "../../SmartMatchingDialogContainer/constants/entityTypes";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import AISuggestionDialogContainer from "../../SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import RecreateLinkDialog from "./RecreateLinkDialog";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModuleViewMoreDialog from "../../dashboard/pages/tickets/ModuleViewMoreDialog";

function SWMLinkPage({ onBeforeSmartMatch }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const zoneFromurl = queryParams.get("selectedZone");
  const eeFromurl = queryParams.get("ee_id");
  const swmFromurl = queryParams.get("swm_id");

  const { networkInterfacesSWM } = useSelector(
    (state) => state.networkInterfacesReducer,
  );

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedSingleVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const [projectID, setProjectID] = useState(null);
  const [variantID, setVariantID] = useState(null);
  const [selectedProjectIsZonal, setSelectedProjectIsZonal] = useState(false);
  const [selectedProjectZonesList, setSelectedProjectZonesList] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const [zoneOptionsList, setZoneOptionsList] = useState([]);
  const [selectedZoneMain, setSelectedZoneMain] = useState(null);
  const [selectedZoneLinked, setSelectedZoneLinked] = useState(null);

  const [eeComponentsMainList, setEEComponentsMainList] = useState([]);
  const [selectedEEMainId, setSelectedEEMainId] = useState(null);
  const [selectedEEComponentMain, setSelectedEEComponentMain] = useState({});

  const [eeComponentsLinkedList, setEEComponentsLinkedList] = useState([]);
  const [selectedEELinkedId, setSelectedEELinkedId] = useState(null);
  const [selectedEEComponentLinked, setSelectedEEComponentLinked] = useState(
    {},
  );

  const [SWMComponentMainList, setSWMComponentMainList] = useState([]);
  const [SWMComponentsLinkedList, setSWMComponentsLinkedList] = useState([]);
  const [selectedMainSWMDetails, setSelectedMainSWMDetails] = useState(null);

  const [leftPage, setLeftPage] = useState(1);
  const [leftRowsPerPage] = useState(25);
  const [rightPage, setRightPage] = useState(1);
  const [rightRowsPerPage] = useState(25);

  const [swmMainCount, setSwmMainCount] = useState(0);
  const [swmLinkCount, setSwmLinkCount] = useState(0);

  const eeRequestCounter = useRef(0);
  const swmRequestCounter = useRef(0);
  const leftZoneTimerRef = useRef(null);
  const rightZoneTimerRef = useRef(null);
  const selectedMainSWMIdRef = useRef(null);

  const [modifiedSWMComponentList, setModifiedSWMComponentList] = useState([]);
  const [modifiedSWMComponentLinkList, setModifiedSWMComponentLinkList] =
    useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);
  const [pendingSWMActionDialogOpen, setPendingSWMActionDialogOpen] =
    useState(false);
  const [selectedPendingSWMAction, setSelectedPendingSWMAction] =
    useState(null);
  const [selectedNetworkInterface, setSelectedNetworkInterface] =
    useState(null);
  const [selectedNetworkItem, setSelectedNetworkItem] = useState(null);
  const [networkDialogOpen, setNetworkDialogOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [linkApiProps, setLinkApiProps] = useState(null);
  const [apiAction, setApiAction] = useState("");
  const [apiDataKey, setApiDataKey] = useState("");
  const [moduleViewMoreType, setModuleViewMoreType] = useState("");
  const [isSWMDialogOpen, setIsSWMDialogOpen] = useState(false);

  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const [openSWMLinkRecreateDialog, setOpenSWMLinkRecreateDialog] =
    useState(false);
  const [recreateSWMLinkList, setRecreateSWMLinkList] = useState([]);
  const [selectedRecreateSWMForLink, setSelectedRecreateSWMForLink] =
    useState(null);

  const handleSmartMatchRequest = useCallback((componentDetails) => {
    const componentName = componentDetails?.description;
    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    }
    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source: [
        {
          entityType: ENTITY.TYPE.SWM,
          entityId: componentDetails?.swm_id,
        },
      ],
      targetEntityTypes: [ENTITY.TYPE.SWM],
      topK: 5,
      minConfidence: 0.1,
      description: componentName,
    });
  }, []);
  useEffect(() => {
    setProjectID(selectedProjectDetails?.project_id);
    setVariantID(selectedSingleVariantDetails?.variant_id);
    setSelectedProjectIsZonal(selectedProjectDetails?.is_zonal);
    setSelectedProjectZonesList(selectedProjectDetails?.project_zones);
    setIsInitialized(false); // Reset initialized flag when project changes
    setSelectedZoneMain(null);
    setSelectedZoneLinked(null);
    setZoneOptionsList([]);

    customConsole({
      title: "🔄 PROJECT CHANGED",
      projectId: selectedProjectDetails?.project_id,
      variantId: selectedSingleVariantDetails?.variant_id,
      isZonal: selectedProjectDetails?.is_zonal,
      zonesCount: selectedProjectDetails?.project_zones?.length,
    });
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  useEffect(() => {
    if (!eeFromurl) return;

    if (String(selectedEEMainId) !== String(eeFromurl)) {
      setSelectedEEMainId(Number(eeFromurl));
    }
  }, [eeFromurl, selectedEEMainId]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_swm_network_interfaces,
        params: {
          network_type: "swm",
        },
        show_toast: false,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (networkDialogOpen && !openSWMLinkRecreateDialog) {
      setSelectedNetworkInterface(null);
    }
  }, [networkDialogOpen, openSWMLinkRecreateDialog]);


  const getEEMainComponentsList = useCallback(
    ({ projectId, zonePosition, variantId }) => {
      let apiDetails = labCar.get_ee_components_new;
      const reqId = ++eeRequestCounter.current;

      customConsole({
        ...apiDetails,
        params: {
          status: true,
          archive: false,
          project_id: projectId,
          zone_position: zonePosition,
          variant_id: variantId,
        },
      });

      return dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            archive: false,
            project_id: projectId,
            zone_position: zonePosition,
            variant_id: variantId,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (reqId !== eeRequestCounter.current) return resp;

        if (resp?.error) {
          setEEComponentsMainList([]);
        } else {
          setEEComponentsMainList(resp?.data || []);
        }
        return resp;
      });
    },
    [dispatch],
  );

  const getEELinkedComponentsList = useCallback(
    ({ projectId, zonePosition, variantId }) => {
      let apiDetails = labCar.get_ee_components_new;
      const reqId = ++eeRequestCounter.current;

      customConsole({
        ...apiDetails,
        params: {
          status: true,
          archive: false,
          project_id: projectId,
          zone_position: zonePosition,
          variant_id: variantId,
        },
      });

      return dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            archive: false,
            project_id: projectId,
            zone_position: zonePosition,
            variant_id: variantId,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (reqId !== eeRequestCounter.current) return resp;

        if (resp?.error) {
          setEEComponentsLinkedList([]);
        } else {
          setEEComponentsLinkedList(resp?.data || []);
        }
        return resp;
      });
    },
    [dispatch],
  );

  const getEEMainLinkedComponentsList = useCallback(
    ({ projectId, variantId }) => {
      let apiDetails = labCar.get_ee_components_new;
      const reqId = ++eeRequestCounter.current;

      customConsole({
        ...apiDetails,
        params: {
          status: true,
          archive: false,
          project_id: projectId,
          variant_id: variantId,
        },
      });

      return dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            archive: false,
            project_id: projectId,
            variant_id: variantId,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (reqId !== eeRequestCounter.current) return resp;

        if (resp?.error) {
          setEEComponentsMainList([]);
          setEEComponentsLinkedList([]);
        } else {
          setEEComponentsMainList(resp?.data || []);
          setEEComponentsLinkedList(resp?.data || []);
        }
        return resp;
      });
    },
    [dispatch],
  );

  const getMainSWModulesList = useCallback(
    ({
      projectId,
      eeId,
      variantId,
      zonePosition,
      pageNo = 1,
      pageSize = leftRowsPerPage,
    }) => {
      let apiDetails = labCar.get_sw_modules_new;
      const reqId = ++swmRequestCounter.current;

      customConsole({
        ...apiDetails,
        params: {
          project_id: projectId,
          ee_id: eeId,
          variantId: variantId,
          ...(swmFromurl ? { swm_id: Number(swmFromurl) } : {}),
          status: true,
          archive: false,
          ...(zonePosition ? { zone_position: zonePosition } : {}),
          page_number: pageNo,
          page_size: pageSize,
        },
      });

      return dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            archive: false,
            project_id: projectId,
            variant_id: variantId,
            ee_id: eeId,
            ...(swmFromurl ? { swm_id: Number(swmFromurl) } : {}),
            ...(zonePosition ? { zone_position: zonePosition } : {}),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (reqId !== swmRequestCounter.current) return resp;

        if (resp?.error) {
          setSWMComponentMainList([]);
          setSelectedMainSWMDetails(null);
          setSwmMainCount(0);
        } else {
          setSWMComponentMainList(resp?.data || []);
          setSwmMainCount(resp?.total_count || resp?.data?.length || 0);
          const currentSelectedId = selectedMainSWMIdRef.current;
          let selectedToSet = null;
          if (currentSelectedId) {
            selectedToSet = resp?.data?.find(
              (item) => String(item?.swm_id) === String(currentSelectedId),
            );
          }
          if (!selectedToSet && resp?.data?.length > 0) {
            selectedToSet = swmFromurl
              ? resp.data.find(
                (item) => String(item?.swm_id) === String(swmFromurl),
              )
              : resp.data[0];
          }
          setSelectedMainSWMDetails(selectedToSet || null);
        }
        return resp;
      });
    },
    [dispatch, swmFromurl, leftRowsPerPage],
  );

  const getLinkedSWModulesList = useCallback(
    ({
      projectId,
      eeId,
      variantId,
      zonePosition,
      pageNo = 1,
      pageSize = rightRowsPerPage,
    }) => {
      let apiDetails = labCar.get_sw_modules_new;

      customConsole({
        ...apiDetails,
        params: {
          status: true,
          archive: false,
          project_id: projectId,
          ee_id: eeId,
          ...(zonePosition ? { zone_position: zonePosition } : {}),
          page_number: pageNo,
          page_size: pageSize,
        },
      });

      return dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            archive: false,
            project_id: projectId,
            variant_id: variantId,
            ee_id: eeId,
            ...(zonePosition ? { zone_position: zonePosition } : {}),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (resp?.error) {
          setSWMComponentsLinkedList([]);
          setSwmLinkCount(0);
        } else {
          setSWMComponentsLinkedList(resp?.data || []);
          setSwmLinkCount(resp?.total_count || resp?.data?.length || 0);
        }
        return resp;
      });
    },
    [dispatch, rightRowsPerPage],
  );

  useEffect(() => {
    selectedMainSWMIdRef.current = selectedMainSWMDetails?.swm_id || null;
  }, [selectedMainSWMDetails]);

  const getSWModulesIntersectionsList = useCallback(
    ({ projectId, eeId, swmId, variantId }) => {
      if (projectId && eeId) {
        let apiDetails = softwareIntersectionAPI.get_software_intersections;

        customConsole({
          ...apiDetails,
          params: {
            status: true,
            project_id: Number(projectId),
            variant_id: variantId,
            ee_id: Number(eeId),
            ...(swmId && { swm_id: Number(swmId) }),
          },
        });

        return dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              status: true,
              project_id: Number(projectId),
              variant_id: variantId,
              ee_id: Number(eeId),
              ...(swmId && { swm_id: Number(swmId) }),
            },
            show_toast: false,
          }),
        );
      }
    },
    [dispatch],
  );

  const handleSWModuleLink = useCallback(
    (selectedLinkedSWModuleDetails) => {
      if (
        projectID &&
        selectedSingleVariantDetails?.variant_id &&
        selectedMainSWMDetails?.ee_id &&
        selectedMainSWMDetails?.swm_id &&
        selectedLinkedSWModuleDetails?.ee_id &&
        selectedLinkedSWModuleDetails?.swm_id
      ) {
        let currentTime = new Date().toISOString();

        dispatch(
          HandleApiActions({
            ...labCar.link_sw_module,
            params: {
              project_id: Number(projectID),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              parent: {
                ee_id: Number(selectedMainSWMDetails?.ee_id),
                swm_id: Number(selectedMainSWMDetails?.swm_id),
              },
              child: {
                ee_id: Number(selectedLinkedSWModuleDetails?.ee_id),
                swm_id: Number(selectedLinkedSWModuleDetails?.swm_id),
              },
              current_time: currentTime,
              network_interface: selectedNetworkInterface,
              module_type: "swm",
            },
            show_toast: true,
          }),
        ).then(() => {
          if (projectID && selectedEEMainId) {
            getMainSWModulesList({
              projectId: projectID,
              eeId: selectedEEMainId,
              variantId: selectedSingleVariantDetails?.variant_id,
              zonePosition: selectedProjectIsZonal
                ? selectedZoneMain
                : undefined,
              pageNo: leftPage,
              pageSize: leftRowsPerPage,
            });
          }
          if (
            projectID &&
            selectedEELinkedId &&
            selectedSingleVariantDetails?.variant_id
          ) {
            getLinkedSWModulesList({
              projectId: projectID,
              eeId: selectedEELinkedId,
              variantId: selectedSingleVariantDetails?.variant_id,
              zonePosition: selectedProjectIsZonal
                ? selectedZoneLinked
                : undefined,
              pageNo: rightPage,
              pageSize: rightRowsPerPage,
            });
          }

          getSWModulesIntersectionsList({
            variantId: selectedSingleVariantDetails?.variant_id,
            projectId: Number(projectID),
            eeId: Number(selectedMainSWMDetails?.ee_id),
            swmId: Number(selectedMainSWMDetails?.swm_id),
          });

          setNetworkDialogOpen(false);
          setSelectedNetworkItem(null);
          setSelectedNetworkInterface(null);
        });
      }
    },
    [
      projectID,
      selectedSingleVariantDetails,
      selectedMainSWMDetails,
      selectedEEMainId,
      selectedEELinkedId,
      selectedNetworkInterface,
      selectedProjectIsZonal,
      selectedZoneMain,
      selectedZoneLinked,
      leftPage,
      leftRowsPerPage,
      rightPage,
      rightRowsPerPage,
      dispatch,
      getMainSWModulesList,
      getLinkedSWModulesList,
      getSWModulesIntersectionsList,
    ],
  );

  const handleSWModuleUnLink = useCallback(
    (selectedLinkedSWModuleDetails) => {
      if (
        projectID &&
        selectedMainSWMDetails?.ee_id &&
        selectedMainSWMDetails?.swm_id &&
        selectedSingleVariantDetails?.variant_id &&
        selectedLinkedSWModuleDetails?.swm_id
      ) {
        let currentTime = new Date().toISOString();

        dispatch(
          HandleApiActions({
            ...labCar.unlink_sw_module,
            params: {
              project_id: Number(projectID),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedMainSWMDetails?.ee_id),
              swm_id: Number(selectedMainSWMDetails?.swm_id),
              removing_swm_id: Number(selectedLinkedSWModuleDetails?.swm_id),
              current_time: currentTime,
            },
            show_toast: true,
          }),
        ).then(() => {
          if (projectID && selectedEEMainId) {
            getMainSWModulesList({
              projectId: projectID,
              eeId: selectedEEMainId,
              variantId: selectedSingleVariantDetails?.variant_id,
              zonePosition: selectedProjectIsZonal
                ? selectedZoneMain
                : undefined,
              pageNo: leftPage,
              pageSize: leftRowsPerPage,
            });
          }
          if (
            projectID &&
            selectedEELinkedId &&
            selectedSingleVariantDetails?.variant_id
          ) {
            getLinkedSWModulesList({
              projectId: projectID,
              eeId: selectedEELinkedId,
              variantId: selectedSingleVariantDetails?.variant_id,
              zonePosition: selectedProjectIsZonal
                ? selectedZoneLinked
                : undefined,
              pageNo: rightPage,
              pageSize: rightRowsPerPage,
            });
          }

          getSWModulesIntersectionsList({
            projectId: Number(projectID),
            eeId: Number(selectedMainSWMDetails?.ee_id),
            variantId: selectedSingleVariantDetails?.variant_id,
            swmId: Number(selectedMainSWMDetails?.swm_id),
          });
        });
      }
    },
    [
      projectID,
      selectedMainSWMDetails,
      selectedSingleVariantDetails,
      selectedEEMainId,
      selectedEELinkedId,
      selectedProjectIsZonal,
      selectedZoneMain,
      selectedZoneLinked,
      leftPage,
      leftRowsPerPage,
      rightPage,
      rightRowsPerPage,
      dispatch,
      getMainSWModulesList,
      getLinkedSWModulesList,
      getSWModulesIntersectionsList,
    ],
  );







  useEffect(() => {
    const getFilteredZones = (zones) =>
      zones
        ?.map((el) => ({
          value: el?.position,
          label: el?.zone_name,
        }))
        ?.sort((a, b) => a.label.localeCompare(b.label));

    const findZoneFromUrl = (filteredZones) => {
      if (!zoneFromurl) return null;

      const lower = String(zoneFromurl).toLowerCase();

      return (
        filteredZones.find((z) => String(z.value) === String(zoneFromurl)) ||
        filteredZones.find((z) => String(z.label).toLowerCase() === lower) ||
        null
      );
    };

    const isValidZone = (zones, value) =>
      zones.some((z) => String(z.value) === String(value));

    const setZone = (setter, value) => {
      if (value !== null) setter(value);
    };

    if (!selectedProjectIsZonal) {
      setSelectedZoneMain(null);
      setSelectedZoneLinked(null);
      return;
    }

    if (!selectedProjectZonesList?.length) return;

    const filteredZones = getFilteredZones(selectedProjectZonesList);
    setZoneOptionsList(filteredZones);

    const urlZone = findZoneFromUrl(filteredZones);
    const fallbackZone = filteredZones?.[0] || null;
    const setVal = urlZone?.value ?? fallbackZone?.value ?? null;

    const isMainValid = isValidZone(filteredZones, selectedZoneMain);
    const isLinkedValid = isValidZone(filteredZones, selectedZoneLinked);

    const shouldSetMain = !selectedZoneMain || !isMainValid;
    const shouldSetLinked = !selectedZoneLinked || !isLinkedValid;

    if (shouldSetMain) {
      setZone(setSelectedZoneMain, setVal);

      if (setVal !== null) {
        customConsole({
          title: "✅ ZONAL PROJECT - Zone Auto-selected",
          zone: urlZone?.label || fallbackZone?.label,
          source: urlZone ? "url" : "first",
        });
      }
    }

    if (shouldSetLinked) {
      setZone(setSelectedZoneLinked, setVal);

      if (setVal !== null) {
        setIsInitialized(true);
      }
    }
  }, [
    selectedProjectIsZonal,
    selectedProjectZonesList,
    selectedZoneMain,
    selectedZoneLinked,
    zoneFromurl,
  ]);

  useEffect(() => {
    if (
      !selectedProjectIsZonal &&
      projectID &&
      selectedSingleVariantDetails?.variant_id &&
      isInitialized === false
    ) {
      setSelectedEEMainId(null);
      setSelectedEEComponentMain({});
      setSelectedEELinkedId(null);
      setSelectedEEComponentLinked({});
      setSWMComponentMainList([]);
      setSWMComponentsLinkedList([]);
      setSelectedMainSWMDetails(null);

      (async () => {
        const resp = await getEEMainLinkedComponentsList({
          projectId: projectID,
          variantId: selectedSingleVariantDetails?.variant_id,
        });

        if (!resp?.error && resp?.data?.length > 0) {
          const byUrl = eeFromurl
            ? resp.data.find((d) => String(d.ee_id) === String(eeFromurl))
            : null;
          const pick = eeFromurl ? byUrl : resp.data[0];

          if (pick) {
            setSelectedEEMainId(pick.ee_id);
            setSelectedEEComponentMain(pick);
            setSelectedEELinkedId(pick.ee_id);
            setSelectedEEComponentLinked(pick);
          }
        }

        setIsInitialized(true);

        customConsole({
          title: "✅ CENTRALIZED PROJECT INITIALIZED",
          projectId: projectID,
          variantId: selectedSingleVariantDetails?.variant_id,
          message: "Fetched EE components for centralized project",
        });
      })();
    }
  }, [
    projectID,
    selectedProjectIsZonal,
    selectedSingleVariantDetails,
    getEEMainLinkedComponentsList,
    isInitialized,
    eeFromurl,
  ]);

  useEffect(() => {
    if (selectedProjectIsZonal) {
      setEEComponentsMainList([]);
      setSelectedEEMainId(null);
      setSelectedEEComponentMain({});
      setSWMComponentMainList([]);
      setSelectedMainSWMDetails(null);
      if (
        projectID &&
        selectedZoneMain &&
        selectedSingleVariantDetails?.variant_id
      ) {
        if (leftZoneTimerRef.current) clearTimeout(leftZoneTimerRef.current);
        leftZoneTimerRef.current = setTimeout(async () => {
          const resp = await getEEMainComponentsList({
            projectId: projectID,
            variantId: selectedSingleVariantDetails?.variant_id,
            zonePosition: selectedZoneMain,
          });
          if (!resp?.error && resp?.data?.length > 0) {
            const byUrl = eeFromurl
              ? resp.data.find((d) => String(d.ee_id) === String(eeFromurl))
              : null;
            const pick = eeFromurl ? byUrl : resp.data[0];

            if (pick) {
              setSelectedEEMainId(pick.ee_id);
              setSelectedEEComponentMain(pick);
            }
          }
        }, 250);
      }
    }
    return () => {
      if (leftZoneTimerRef.current) {
        clearTimeout(leftZoneTimerRef.current);
        leftZoneTimerRef.current = null;
      }
    };
  }, [
    selectedZoneMain,
    selectedProjectIsZonal,
    projectID,
    selectedSingleVariantDetails,
    getEEMainComponentsList,
    eeFromurl,
  ]);

  useEffect(() => {
    if (selectedProjectIsZonal) {
      setEEComponentsLinkedList([]);
      setSelectedEELinkedId(null);
      setSelectedEEComponentLinked({});
      setSWMComponentsLinkedList([]);
      if (
        projectID &&
        selectedZoneLinked &&
        selectedSingleVariantDetails?.variant_id
      ) {
        if (rightZoneTimerRef.current) clearTimeout(rightZoneTimerRef.current);
        rightZoneTimerRef.current = setTimeout(async () => {
          const resp = await getEELinkedComponentsList({
            projectId: projectID,
            zonePosition: selectedZoneLinked,
            variantId: selectedSingleVariantDetails?.variant_id,
          });
          if (!resp?.error && resp?.data?.length > 0) {
            const first = resp.data[0];
            if (first) {
              setSelectedEELinkedId(first.ee_id);
              setSelectedEEComponentLinked(first);
            }
          }
        }, 250);
      }
      return () => {
        if (rightZoneTimerRef.current) {
          clearTimeout(rightZoneTimerRef.current);
          rightZoneTimerRef.current = null;
        }
      };
    }
  }, [
    selectedZoneLinked,
    selectedProjectIsZonal,
    projectID,
    selectedSingleVariantDetails,
    getEELinkedComponentsList,
  ]);


  const leftEEAutocompleteParams = useMemo(() => {
    return {
      ...(projectID &&
        variantID &&
        (!selectedProjectIsZonal || selectedZoneMain)
        ? {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ...(selectedProjectIsZonal && { zone_position: selectedZoneMain }),
          status: true,
          archive: false,
          page_number: 1,
          page_size: 10,
        }
        : {}),
    };
  }, [projectID, variantID, selectedProjectIsZonal, selectedZoneMain]);

  const leftSWMSearchParams = useMemo(() => {
    return {
      ...(projectID && variantID && selectedEEMainId
        ? {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ee_id: Number(selectedEEMainId),
          ...(swmFromurl ? { swm_id: Number(swmFromurl) } : {}),
          ...(selectedProjectIsZonal && { zone_position: selectedZoneMain }),
          status: true,
          archive: false,
          page_number: leftPage,
          page_size: leftRowsPerPage,
        }
        : {}),
    };
  }, [
    projectID,
    variantID,
    selectedEEMainId,
    selectedProjectIsZonal,
    selectedZoneMain,
    leftPage,
    leftRowsPerPage,
    swmFromurl,
  ]);

  const rightEEAutocompleteParams = useMemo(() => {
    return {
      ...(projectID &&
        variantID &&
        (!selectedProjectIsZonal || selectedZoneLinked)
        ? {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ...(selectedProjectIsZonal && {
            zone_position: selectedZoneLinked,
          }),
          status: true,
          archive: false,
          page_number: 1,
          page_size: 10,
        }
        : {}),
    };
  }, [projectID, variantID, selectedProjectIsZonal, selectedZoneLinked]);

  const rightSWMSearchParams = useMemo(() => {
    return {
      ...(projectID && variantID && selectedEELinkedId
        ? {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ee_id: Number(selectedEELinkedId),
          ...(selectedProjectIsZonal && {
            zone_position: selectedZoneLinked,
          }),
          status: true,
          archive: false,
          page_number: rightPage,
          page_size: rightRowsPerPage,
        }
        : {}),
    };
  }, [
    projectID,
    variantID,
    selectedEELinkedId,
    selectedProjectIsZonal,
    selectedZoneLinked,
    rightPage,
    rightRowsPerPage,
  ]);

  useEffect(() => {
    if (eeComponentsMainList?.length > 0 && !selectedEEMainId) {
      const matchingComponent = eeFromurl
        ? eeComponentsMainList?.find(
          (item) => String(item?.ee_id) === String(eeFromurl),
        )
        : null;

      if (matchingComponent) {
        setSelectedEEMainId(matchingComponent?.ee_id);
        setSelectedEEComponentMain(matchingComponent);
        customConsole({
          title: "✅ LEFT EE AUTO-SELECTED (from URL)",
          eeId: matchingComponent?.ee_id,
          description: matchingComponent?.description,
        });
      } else {
        const first = eeComponentsMainList?.[0];
        if (first) {
          setSelectedEEMainId(first.ee_id);
          setSelectedEEComponentMain(first);
          customConsole({
            title: "✅ LEFT EE AUTO-SELECTED (first item)",
            eeId: first.ee_id,
            description: first.description,
            isZonal: selectedProjectIsZonal,
          });
        }
      }
    }
  }, [
    eeComponentsMainList,
    eeFromurl,
    selectedEEMainId,
    selectedProjectIsZonal,
  ]);

  useEffect(() => {
    if (!selectedEEMainId || eeComponentsMainList?.length === 0) return;

    const matchingComponent = eeComponentsMainList.find(
      (item) => String(item?.ee_id) === String(selectedEEMainId),
    );

    if (matchingComponent) {
      setSelectedEEComponentMain(matchingComponent);
    }
  }, [eeComponentsMainList, selectedEEMainId]);

  useEffect(() => {

    console.log(
      "[SWM] eeComponentsMainList ids:",
      eeComponentsMainList?.map((item) => item?.ee_id),
    );
  }, [eeFromurl, selectedEEMainId, eeComponentsMainList]);

  useEffect(() => {
    if (eeComponentsLinkedList?.length > 0 && !selectedEELinkedId) {
      const firstLinked = eeComponentsLinkedList?.[0];
      if (firstLinked) {
        setSelectedEELinkedId(firstLinked.ee_id);
        setSelectedEEComponentLinked(firstLinked);
        customConsole({
          title: "✅ RIGHT EE AUTO-SELECTED (first item)",
          eeId: firstLinked.ee_id,
          description: firstLinked.description,
          isZonal: selectedProjectIsZonal,
        });
      }
    }
  }, [eeComponentsLinkedList, selectedEELinkedId, selectedProjectIsZonal]);


  useEffect(() => {
    if (selectedEEMainId) {
      setSelectedMainSWMDetails(null);
      setSWMComponentMainList([]);
      setSwmMainCount(0);
      setLeftPage(1);
    }
  }, [selectedEEMainId]);

  useEffect(() => {
    if (selectedEELinkedId) {
      setSWMComponentsLinkedList([]);
      setSwmLinkCount(0);
      setRightPage(1);
    }
  }, [selectedEELinkedId]);

  useEffect(() => {
    if (
      projectID &&
      variantID &&
      selectedEEMainId &&
      (!selectedProjectIsZonal || selectedZoneMain)
    ) {
      getMainSWModulesList({
        projectId: projectID,
        eeId: selectedEEMainId,
        variantId: variantID,
        zonePosition: selectedProjectIsZonal ? selectedZoneMain : undefined,
        pageNo: leftPage,
        pageSize: leftRowsPerPage,
      });
    }
  }, [
    projectID,
    variantID,
    selectedEEMainId,
    selectedProjectIsZonal,
    selectedZoneMain,
    leftPage,
    leftRowsPerPage,
    getMainSWModulesList,
  ]);

  useEffect(() => {
    if (
      projectID &&
      variantID &&
      selectedEELinkedId &&
      (!selectedProjectIsZonal || selectedZoneLinked)
    ) {
      getLinkedSWModulesList({
        projectId: projectID,
        eeId: selectedEELinkedId,
        variantId: variantID,
        zonePosition: selectedProjectIsZonal ? selectedZoneLinked : undefined,
        pageNo: rightPage,
        pageSize: rightRowsPerPage,
      });
    }
  }, [
    projectID,
    variantID,
    selectedEELinkedId,
    selectedProjectIsZonal,
    selectedZoneLinked,
    rightPage,
    rightRowsPerPage,
    getLinkedSWModulesList,
  ]);

  useEffect(() => {
    if (SWMComponentMainList?.length > 0) {
      const matchingSwm = swmFromurl
        ? SWMComponentMainList.find(
          (item) => String(item?.swm_id) === String(swmFromurl),
        )
        : null;

      if (matchingSwm) {
        setSelectedMainSWMDetails(matchingSwm);
      } else if (
        selectedMainSWMDetails === null ||
        selectedMainSWMDetails === undefined
      ) {
        setSelectedMainSWMDetails(SWMComponentMainList?.[0]);
      }
    }
  }, [SWMComponentMainList, swmFromurl, selectedMainSWMDetails]);

  useEffect(() => {
    if (selectedMainSWMDetails?.swm_id) {
      getSWModulesIntersectionsList({
        variantId: selectedSingleVariantDetails?.variant_id,
        projectId: Number(projectID),
        eeId: Number(selectedEEMainId),
        swmId: Number(selectedMainSWMDetails?.swm_id),
      });
    }
  }, [
    projectID,
    selectedEEMainId,
    selectedMainSWMDetails?.swm_id,
    selectedSingleVariantDetails,
    getSWModulesIntersectionsList,
  ]);


  useEffect(() => {
    const modifiedList = SWMComponentMainList.map((item) => ({
      data: item,
      id: item.swm_id,
      cardDataArray: [
        {
          key: "Number",
          value: (
            <CustomToolTip
              type="string"
              length={50}
              style={{
                ...componentStyle.font.text,
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={`Name: ${item?.name ?? ""}`}
            >
              {`${item?.name ?? ""}`}
            </CustomToolTip>
          ),
          keyStyle: { ...componentStyle.font.text, fontWeight: "bold" },
          valueStyle: { ...componentStyle.font.text, fontWeight: "bold" },
        },
        {
          key: "Version",
          value: (
            <CustomToolTip
              type="string"
              length={50}
              style={{
                ...componentStyle.font.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={`Version: ${item?.version ?? ""}`}
            >
              {`${item?.version ?? ""}`}
            </CustomToolTip>
          ),
        },
        {
          key: "Description",
          value: (
            <CustomToolTip
              type="string"
              length={50}
              style={{
                ...componentStyle.font.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={`Description: ${item?.description ?? ""}`}
            >
              {`${item?.description ?? ""}`}
            </CustomToolTip>
          ),
        }
      ],
    }));
    setModifiedSWMComponentList(modifiedList);
  }, [SWMComponentMainList]);

  useEffect(() => {
    const filteredList = SWMComponentsLinkedList.filter(
      (item) => item.swm_id !== selectedMainSWMDetails?.swm_id,
    );
    const modifiedList = filteredList.map((item) => {
      const isLinked = item?.linked_swm_ids?.includes(
        selectedMainSWMDetails?.swm_id,
      );
      return {
        data: item,
        id: item.swm_id,
        cardDataArray: [
          {
            key: "Number",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Name: ${item?.name ?? ""}`}
              >
                {`${item?.name ?? ""}`}
              </CustomToolTip>
            ),
            keyStyle: { ...componentStyle.font.text, fontWeight: "bold" },
            valueStyle: { ...componentStyle.font.text, fontWeight: "bold" },
          },
          {
            key: "Version",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Version: ${item?.version ?? ""}`}
              >
                {`${item?.version ?? ""}`}
              </CustomToolTip>
            ),
          },
          {
            key: "Description",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Description: ${item?.description ?? ""}`}
              >
                {`${item?.description ?? ""}`}
              </CustomToolTip>
            ),
          },
        ],
        cardStyle: isLinked
          ? {
              border: "1px solid #4CAF50",
              backgroundColor: "rgba(76, 175, 80, 0.05)",
            }
          : {},
      };
    });
    setModifiedSWMComponentLinkList(modifiedList);
  }, [SWMComponentsLinkedList, selectedMainSWMDetails]);


  const handleNetworkInterfaceSelect = (item) => {
    handleSWModuleLink(item);
    setNetworkDialogOpen(false);
    setSelectedNetworkItem(null);
    setSelectedNetworkInterface(null);
  };

  const handleCloseNetworkInterface = () => {
    setNetworkDialogOpen(false);
    setSelectedNetworkItem(null);
    setSelectedNetworkInterface(null);
  };

  const handleOpenNetworkInterface = useCallback((item) => {
    setSelectedNetworkItem(item);
    setNetworkDialogOpen(true);
  }, []);

  const handleDialogClose = () => {
    setDialogOpen(false);
    const params = new URLSearchParams(location.search);
    params.delete("swm_id");
    if (!params.get("ee_id")) {
      params.set(
        "ee_id",
        selectedEEMainId || selectedEEComponentMain?.ee_id || "",
      );
    }
    if (!params.get("selectedZone")) {
      params.set("selectedZone", selectedZoneMain || "");
    }
    params.set("tabFromurl", "1");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleActionType2DialogOpen = () => {
    setActionType2DialogOpen(true);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handlePendingSWMActionDialogClose = () => {
    setPendingSWMActionDialogOpen(false);
    setSelectedPendingSWMAction(null);
  };

  const handleRedirectionToSWMAction = () => {
    const selectedSWM = selectedPendingSWMAction || selectedMainSWMDetails;
    const selectedZoneDetails = selectedProjectZonesList?.find(
      (zone) => Number(zone?.position) === Number(selectedZoneMain),
    );
    const params = new URLSearchParams();

    if (selectedSWM?.ee_id || selectedEEMainId) {
      params.set("ee_id", selectedSWM?.ee_id || selectedEEMainId);
    }
    if (selectedSWM?.swm_id) {
      params.set("swm_id", selectedSWM.swm_id);
    }
    params.set("actionMode", "swmAction");
    if (selectedZoneMain) {
      params.set(
        "selectedZone",
        selectedZoneDetails?.zone_name || selectedZoneMain,
      );
      params.set("zonePosition", selectedZoneMain);
    }
    if (leftPage) {
      params.set("page", leftPage);
    }

    navigate(`/dashboard/v2/EESWM?${params.toString()}`);
    handlePendingSWMActionDialogClose();
  };

  const getSWMActionTypeTooltip = (actionType) => {
    switch (Number(actionType)) {
      case 1:
        return "Action Pending in Software to Software linking";
      case 2:
        return "Action Pending in Software to Legal linking";
      case 4:
        return "Action required in Software Module Attribute";
      case 5:
        return "Action pending in Software Module Version";
      default:
        return "";
    }
  };

  const handleSWMActionTypeClick = useCallback((event, item) => {
    event.stopPropagation();
    setSelectedMainSWMDetails(item);

    if (Number(item?.action_type) === 1) {
      setDialogOpen(true);
    } else if (Number(item?.action_type) === 2) {
      setActionType2DialogOpen(true);
    } else if ([4, 5].includes(Number(item?.action_type))) {
      setSelectedPendingSWMAction(item);
      setPendingSWMActionDialogOpen(true);
    }
  }, []);

  const handleZoneMainChange = (e) => {
    setSelectedZoneMain(e.target.value);
  };

  const handleZoneLinkedChange = (e) => {
    setSelectedZoneLinked(e.target.value);
  };

  const confirmUpdateAndRedirect = (selectedMainSWModuleDetails) => {
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(projectID),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEMainId),
      swm_id: Number(selectedMainSWModuleDetails?.swm_id),
      is_dependency_linking: true,
    };
    dispatch(
      HandleApiActions({
        ...labCar.update_action_type,
        params: reqObj,
        show_toast: true,
      }),
    )
      .then((response) => {
        if (!response?.error) {
          getMainSWModulesList({
            projectId: projectID,
            eeId: selectedEEMainId,
            variantId: selectedSingleVariantDetails?.variant_id,
          });
          handleDialogClose();
          handleActionType2DialogOpen();
        }
      })
      .catch((error) => {
        customConsole(error);
      });
  };

  const handleRecreateDialogOpen = useCallback(
    (eeDetails) => {
      dispatch(
        HandleApiActions({
          ...labCar.get_parent_swm_link,
          params: {
            project_id: selectedProjectDetails?.project_id,
            parent_project_id: selectedProjectDetails?.parent_project_id,
            swm_id: eeDetails?.swm_id,
            parent_swm_id: eeDetails?.parent_swm_id,
          },
        }),
      ).then((parentSWMLinkResponse) => {
        if (parentSWMLinkResponse?.error) {
          setRecreateSWMLinkList([]);
        } else {
          let responseList = parentSWMLinkResponse?.data || [];
          let tableRows = responseList.map((swm) => {
            return {
              id: swm.swm_id,
              component: {
                primary: {
                  key: "Name:",
                  value: swm.name,
                },
                secondary: [
                  {
                    key: "version:",
                    value: swm.version,
                  },
                  {
                    key: "Description:",
                    value: swm.description,
                  },
                ],
              },
              raw: swm,
            };
          });
          setRecreateSWMLinkList(tableRows);
        }
        setOpenSWMLinkRecreateDialog(true);
      });
    },
    [
      dispatch,
      selectedProjectDetails?.project_id,
      selectedProjectDetails?.parent_project_id,
    ],
  );

  const closeRecreateDialog = () => {
    setOpenSWMLinkRecreateDialog(false);

    if (projectID && selectedEEMainId) {
      getMainSWModulesList({
        projectId: projectID,
        eeId: selectedEEMainId,
        variantId: selectedSingleVariantDetails?.variant_id,
        zonePosition: selectedProjectIsZonal ? selectedZoneMain : undefined,
        pageNo: leftPage,
        pageSize: leftRowsPerPage,
      });
    }
    if (
      projectID &&
      selectedEELinkedId &&
      selectedSingleVariantDetails?.variant_id
    ) {
      getLinkedSWModulesList({
        projectId: projectID,
        eeId: selectedEELinkedId,
        variantId: selectedSingleVariantDetails?.variant_id,
        zonePosition: selectedProjectIsZonal ? selectedZoneLinked : undefined,
        pageNo: rightPage,
        pageSize: rightRowsPerPage,
      });
    }

    getSWModulesIntersectionsList({
      variantId: selectedSingleVariantDetails?.variant_id,
      projectId: Number(projectID),
      eeId: Number(selectedMainSWMDetails?.ee_id),
      swmId: Number(selectedMainSWMDetails?.swm_id),
    });
  };

  const handleOnClickRecreateLink = (selectedSWM) => {
    setNetworkDialogOpen(true);
    setSelectedRecreateSWMForLink(selectedSWM);

    setSelectedNetworkInterface(selectedSWM?.parent_network_interface);
  };

  const handleRecreateLink = () => {
    const reqObj = {
      project_id: Number(projectID),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      parent: {
        ee_id: Number(selectedMainSWMDetails?.ee_id),
        swm_id: Number(selectedMainSWMDetails?.swm_id),
      },
      child: {
        ee_id: Number(selectedRecreateSWMForLink?.ee_id),
        swm_id: Number(selectedRecreateSWMForLink?.swm_id),
      },
      current_time: new Date().toISOString(),
      network_interface: selectedNetworkInterface,
      module_type: "swm",
      parent_parent_swm_id: selectedMainSWMDetails?.parent_swm_id,
      parent_child_swm_id: selectedRecreateSWMForLink?.parent_swm_id,
      session_id: selectedRecreateSWMForLink?.session_id,
    };
    let apiDetails = labCar.recreate_swm_link;
    dispatch(
      HandleApiActions({
        ...apiDetails,
        params: reqObj,
        show_toast: true,
      }),
    ).then((resp) => {
      if (!resp?.error) {
        setNetworkDialogOpen(false);
        handleRecreateDialogOpen(selectedMainSWMDetails);
      }
    });
  };

  const toggleSWMDialog = useCallback(
    (newOpen) => () => {
      setIsSWMDialogOpen(newOpen);
    },
    [],
  );

  const handleSWMViewButtonClick = useCallback(
    (item) => {
      setSelectedComponent({
        swm_details: item,
        ee_details: { ee_id: item?.ee_id },
      });
      setApiAction(softwareIntersectionAPI.get_software_intersections);
      setLinkApiProps({
        swm_id: item?.swm_id,
        ee_id: item?.ee_id,
        archive: false,
      });
      setApiDataKey("linked_swm_details");
      setModuleViewMoreType("SWM");
      setIsSWMDialogOpen(true);
    },
    [],
  );

  const isLeftEESelectedInList = eeComponentsMainList?.some(
    (ee) => String(ee?.ee_id) === String(selectedEEMainId),
  );
  let leftEEValueObj = {};
  if (selectedProjectIsZonal || isLeftEESelectedInList) {
    leftEEValueObj = selectedEEComponentMain;
  }

  const isRightEESelectedInList = eeComponentsLinkedList?.some(
    (ee) => String(ee?.ee_id) === String(selectedEELinkedId),
  );
  let rightEEValueObj = {};
  if (selectedProjectIsZonal || isRightEESelectedInList) {
    rightEEValueObj = selectedEEComponentLinked;
  }

  const renderLeftCardActionIcons = useCallback(
    (item) => {
      const actionType = Number(item?.action_type);
      const actionTypeTooltip = getSWMActionTypeTooltip(actionType);
      return (
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {[1, 2, 4, 5].includes(actionType) && (
              <CustomToolTip
                type="component"
                title={actionTypeTooltip}
              >
                <IconButton
                  size="small"
                  sx={{ color: "black", p: 0.5 }}
                  onClick={(event) => handleSWMActionTypeClick(event, item)}
                >
                  <ErrorOutlineIcon sx={{ fontSize: "15px" }} />
                </IconButton>
              </CustomToolTip>
            )}
            {item.parent_swm_id && (
              <CustomToolTip type="component" title="Recreate Link">
                <IconButton
                  size="small"
                  sx={{ color: "black" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMainSWMDetails(item);
                    handleRecreateDialogOpen(item);
                  }}
                >
                  <RefreshIcon sx={{ fontSize: "15px", color: "black" }} />
                </IconButton>
              </CustomToolTip>
            )}
            <CustomToolTip type="component" title="Initiate Smart-matching">
              <IconButton
                size="small"
                sx={{ color: "black" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMainSWMDetails(item);
                  handleSmartMatchRequest(item);
                }}
              >
                <AutoAwesomeIcon
                  sx={{ fontSize: "15px", color: "black" }}
                />
              </IconButton>
            </CustomToolTip>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={() => {
                handleSWMViewButtonClick(item);
              }}
            >
              <VisibilityIcon sx={{ fontSize: "15px", color: "black" }} />
            </IconButton>
          </Box>
        </Box>
      );
    },
    [handleRecreateDialogOpen, handleSmartMatchRequest, handleSWMActionTypeClick, handleSWMViewButtonClick],
  );

  const renderRightCardActionIcons = useCallback(
    (item) => {
      return (
        <Box sx={{ position: "relative" }}>
          {selectedMainSWMDetails && (
            <CustomToolTip
              type="component"
              title={
                item?.linked_swm_ids?.includes(
                  selectedMainSWMDetails?.swm_id,
                )
                  ? "Unlink"
                  : "Link"
              }
            >
              <IconButton
                size="small"
                sx={{
                  fontSize: "12px",
                  color: "black",
                  position: "absolute",
                  top: 2,
                  right: 2,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    item?.linked_swm_ids?.includes(
                      selectedMainSWMDetails?.swm_id,
                    )
                  ) {
                    handleSWModuleUnLink(item);
                  } else if (
                    networkInterfacesSWM &&
                    networkInterfacesSWM.length > 0
                  ) {
                    handleOpenNetworkInterface(item);
                  } else {
                    handleSWModuleLink(item);
                  }
                }}
              >
                {item?.linked_swm_ids?.includes(
                  selectedMainSWMDetails?.swm_id,
                ) ? (
                  <RemoveCircleOutlineIcon sx={{ fontSize: 15 }} />
                ) : (
                  <ControlPointIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </CustomToolTip>
          )}
        </Box>
      );
    },
    [
      selectedMainSWMDetails,
      handleSWModuleUnLink,
      networkInterfacesSWM,
      handleOpenNetworkInterface,
      handleSWModuleLink,
    ],
  );

  return (
    <Box key={swmFromurl}>
      <ComponentLinkTemplate
        leftCardHeader={
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                mb: 0.5,
              }}
            >
              <Typography>Select Module</Typography>
            </Box>
            <Box
              display="flex"
              gap={1}
              sx={{
                mt: 0.5,
                justifyContent: "space-between",
                alignItems: "center",
                mr: selectedProjectIsZonal ? 1 : 0,
              }}
            >
              {selectedProjectIsZonal && (
                <Box sx={{ flex: "0 0 40%" }}>
                  <CustomSelectV2
                    label={"Select Zone"}
                    optionsList={zoneOptionsList}
                    selectedValue={selectedZoneMain}
                    handleOnChange={handleZoneMainChange}
                    dark={true}
                    showLabel={false}
                    sx={{
                      height: 40,
                      border: "1px solid #ced4da",
                      borderRadius: 1,
                      background: "#fff",
                      ...componentStyle.searchField.textField,
                    }}
                  />
                </Box>
              )}
              <Box sx={{ flex: selectedProjectIsZonal ? "0 0 60%" : "1" }}>
                <CustomAutoCompleteWithAPIV2
                  key={`left-ee-${variantID}-${selectedZoneMain}`}
                  apiAction={labCar.get_ee_components_new}
                  searchKey={"description"}
                  placeholder={"Search EE Component"}
                  skipInitialSearch={false}
                  firstItemDefaultSelect={!eeFromurl}
                  getSelectedItem={(selectedEE) => {
                    setSelectedEEComponentMain(selectedEE);
                    setSelectedEEMainId(selectedEE?.ee_id);
                  }}
                  displayKey="description"
                  valueObj={leftEEValueObj}
                  enableDarkMode={true}
                  txtFldStyle={componentStyle.searchField.textField}
                  sx={{
                    height: 40,
                    border: "1px solid #ced4da",
                    borderRadius: 1,
                    background: "#fff",
                    ...componentStyle.searchField.textField,
                  }}
                  skipApiCall={!projectID || !variantID}
                  params={leftEEAutocompleteParams}
                />
              </Box>
            </Box>
            <Box sx={{ width: "100%", mt: 0.5 }}>
              <CustomSearchbarWithApiV2
                key={`left-swm-${selectedEEComponentMain?.ee_id}`}
                apiAction={labCar.get_sw_modules_new}
                searchKey={"name"}
                placeholder={"Search Number"}
                getDataOnSearch={(data, resp) => {
                  if (resp.error) {
                    setSWMComponentMainList([]);
                    setSwmMainCount(0);
                  } else {
                    setSWMComponentMainList(resp.data);
                    setSwmMainCount(resp.total_count || resp.data?.length || 0);
                  }
                }}
                skipApiCall={
                  !projectID ||
                  !variantID ||
                  !selectedEEMainId ||
                  (selectedProjectIsZonal && !selectedZoneMain)
                }
                skipInitialSearch={true}
                params={leftSWMSearchParams}
                iconColor={"white"}
                txtFldStyle={componentStyle.searchField.textField}
                sx={{
                  height: 40,
                  border: "1px solid #ced4da",
                  borderRadius: 1,
                  background: "#fff",
                  ...componentStyle.searchField.textField,
                }}
              />
            </Box>
          </Box>
        }
        leftDataList={modifiedSWMComponentList}
        leftHandleSelectedComponent={(selectedObj) => {
          setSelectedMainSWMDetails(selectedObj.data);
        }}
        leftSelectedComponentDetails={selectedMainSWMDetails}
        leftRenderCardActionIcons={renderLeftCardActionIcons}
        leftPage={leftPage}
        leftRowsPerPage={leftRowsPerPage}
        leftHandlePageChange={setLeftPage}
        leftTotalCount={swmMainCount}
        leftCardID={"swm_id"}
        rightCardHeader={
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              sx={{ mb: 0.5 }}
            >
              <Typography>Link to Module</Typography>
            </Box>
            <Box
              display="flex"
              gap={1}
              sx={{
                mt: 0.5,
                justifyContent: "space-between",
                alignItems: "center",
                mr: selectedProjectIsZonal ? 1 : 0,
              }}
            >
              {selectedProjectIsZonal && (
                <Box sx={{ flex: "0 0 40%" }}>
                  <CustomSelectV2
                    label={"Select Zone"}
                    optionsList={zoneOptionsList}
                    selectedValue={selectedZoneLinked}
                    handleOnChange={handleZoneLinkedChange}
                    dark={true}
                    showLabel={false}
                    sx={{
                      height: 40,
                      border: "1px solid #ced4da",
                      borderRadius: 1,
                      background: "#fff",
                      ...componentStyle.searchField.textField,
                    }}
                  />
                </Box>
              )}
              <Box sx={{ flex: selectedProjectIsZonal ? "0 0 60%" : "1" }}>
                <CustomAutoCompleteWithAPIV2
                  key={`right-ee-${variantID}-${selectedZoneLinked}`}
                  apiAction={labCar.get_ee_components_new}
                  searchKey={"description"}
                  placeholder={"Search EE Component"}
                  firstItemDefaultSelect={!eeFromurl}
                  skipInitialSearch={false}
                  getSelectedItem={(selectedEE) => {
                    setSelectedEEComponentLinked(selectedEE);
                    setSelectedEELinkedId(selectedEE?.ee_id);
                  }}
                  displayKey="description"
                  valueObj={rightEEValueObj}
                  enableDarkMode={true}
                  txtFldStyle={componentStyle.searchField.textField}
                  sx={{
                    height: 40,
                    border: "1px solid #ced4da",
                    borderRadius: 1,
                    background: "#fff",
                    ...componentStyle.searchField.textField,
                  }}
                  skipApiCall={!projectID || !variantID}
                  params={rightEEAutocompleteParams}
                />
              </Box>
            </Box>
            <Box sx={{ width: "100%", mt: 0.5 }}>
              <CustomSearchbarWithApiV2
                key={`right-swm-${selectedEEComponentLinked?.ee_id}`}
                apiAction={labCar.get_sw_modules_new}
                searchKey={"name"}
                placeholder={"Search Number"}
                getDataOnSearch={(data, resp) => {
                  if (resp.error) {
                    setSWMComponentsLinkedList([]);
                    setSwmLinkCount(0);
                  } else {
                    setSWMComponentsLinkedList(resp.data);
                    setSwmLinkCount(resp.total_count || resp.data?.length || 0);
                  }
                }}
                skipApiCall={
                  !projectID ||
                  !variantID ||
                  !selectedEELinkedId ||
                  (selectedProjectIsZonal && !selectedZoneLinked)
                }
                skipInitialSearch={true}
                params={rightSWMSearchParams}
                iconColor={"white"}
                txtFldStyle={componentStyle.searchField.textField}
                sx={{
                  height: 40,
                  border: "1px solid #ced4da",
                  borderRadius: 1,
                  background: "#fff",
                  ...componentStyle.searchField.textField,
                }}
              />
            </Box>
          </Box>
        }
        rightDataList={modifiedSWMComponentLinkList}
        rightPage={rightPage}
        rightRowsPerPage={rightRowsPerPage}
        rightHandlePageChange={setRightPage}
        rightTotalCount={swmLinkCount}
        rightCardStyleProp={(item) => item.cardStyle || {}}
        rightRenderCardActionIcons={renderRightCardActionIcons}
      />
      <CustomDialogBoxV2
        open={networkDialogOpen}
        onClose={handleCloseNetworkInterface}
        title={"Network Interface"}
        actionButtonStyle="continuous"
        successButtonName="Confirm"
        onClickSuccess={() => {
          if (openSWMLinkRecreateDialog) {
            handleRecreateLink();
          } else {
            handleNetworkInterfaceSelect(selectedNetworkItem);
          }
        }}
        rejectButtonName={"Cancel"}
        onClickReject={handleCloseNetworkInterface}
        onDisabled={!selectedNetworkInterface}
      >
        <Box sx={{ width: "350px" }}>
          <Stack spacing={2}>
            {networkInterfacesSWM?.length > 0 ? (
              <Stack>
                <CustomSelectV2
                  label={"Select The Interface"}
                  optionsList={networkInterfacesSWM.map((ni) => ({
                    label: ni.name,
                    value: ni.name,
                  }))}
                  selectedValue={selectedNetworkInterface}
                  handleOnChange={(e) => {
                    setSelectedNetworkInterface(e.target.value);
                  }}
                  required={true}
                  placeholder={false}
                />
              </Stack>
            ) : (
              <Typography sx={{ ...componentStyle.font.cardText }}>
                No network interfaces available.
              </Typography>
            )}
          </Stack>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={"Action Required"}
        actionButtonStyle="continuous"
        successButtonName={"Yes"}
        rejectButtonName={"No"}
        onClickSuccess={() => {
          confirmUpdateAndRedirect(selectedMainSWMDetails);
        }}
        onClickReject={handleDialogClose}
      >
        <Box sx={{ width: "300px" }}>
          <Typography>
            Are you sure the dependency linking of Software Module is completed?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={actionType2DialogOpen}
        onClose={handleActionType2DialogClose}
        title={"Action Required"}
        actionButtonStyle="continuous"
        successButtonName={"Link Now"}
        rejectButtonName={"Link Later"}
        onClickSuccess={() => {
          navigate(
            `/dashboard/v2/type-approval/link?swm_id=${selectedMainSWMDetails?.swm_id
            }&ee_id=${selectedEEMainId}&zone=${selectedZoneMain}&tab=${3}`,
          );
        }}
        onClickReject={handleActionType2DialogClose}
      >
        <Box sx={{ width: "300px" }}>
          <Typography>
            Software Module intersection linking is pending!
          </Typography>
        </Box>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        open={pendingSWMActionDialogOpen}
        onClose={handlePendingSWMActionDialogClose}
        title={"Action"}
        actionButtonStyle="continuous"
        successButtonName={"Link Now"}
        onClickSuccess={handleRedirectionToSWMAction}
        onClickReject={handlePendingSWMActionDialogClose}
        rejectButtonName={"Link Later"}
      >
        <Box sx={{ width: "350px", height: "50px" }}>
          <Typography>
            {Number(selectedPendingSWMAction?.action_type) === 4
              ? "Action pending in Software Module Attribute"
              : "Action pending in Software Module Version"}
          </Typography>
        </Box>
      </CustomDialogBoxV2>
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleOpenNetworkInterface}
          onUnlinkRequest={handleSWModuleUnLink}
        />
      )}

      {openSWMLinkRecreateDialog && (
        <RecreateLinkDialog
          open={openSWMLinkRecreateDialog}
          onClose={closeRecreateDialog}
          entity={"EE"}
          linkList={recreateSWMLinkList}
          handleLink={handleOnClickRecreateLink}
        />
      )}
      <ModuleViewMoreDialog
        isEEDialogOpen={isSWMDialogOpen}
        toggleEEDialog={toggleSWMDialog}
        selectedComponent={selectedComponent}
        linkApiProps={linkApiProps}
        apiAction={apiAction}
        apiDataKey={apiDataKey}
        type={moduleViewMoreType}
        projectId={projectID}
        variantId={variantID}
        showSupplier={false}
      />
    </Box>
  );
}

SWMLinkPage.propTypes = {
  onBeforeSmartMatch: PropTypes.func,
};

export default SWMLinkPage;
